import { api } from './api';

export type TeamRole = 'owner' | 'admin' | 'editor' | 'viewer' | 'client' | 'billing_admin';

export interface TeamMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: TeamRole;
  /** Paused: over the plan's seats after a downgrade (D-01), kept but without access. */
  status: 'active' | 'paused';
  joinedAt: string;
}

export type InviteState = 'pending' | 'expired' | 'revoked' | 'declined' | 'accepted';

/** An invitation as the Team screen lists it (M-01). */
export interface TeamInvite {
  id: string;
  email: string;
  role: TeamRole;
  state: InviteState;
  createdAt: string;
  expiresAt: string;
  lastSentAt: string | null;
}

/** What sending (or resending) an invitation answers. */
export interface InviteSent {
  invite: TeamInvite;
  /** True only when the email went to the provider; otherwise the link has to be shared by hand. */
  emailSent: boolean;
  link: string;
}

/** Seats of the brand in view: "2 of 3 seats" (AL-01, AL-37). */
export interface TeamSeats {
  plan: string;
  limit: number;
  members: number;
  pending: number;
  /** Members + pending invites over the limit, after a downgrade. */
  over: number;
}

/** The public invite page (no sign-in): what the link opens, never the full address. */
export interface InvitePreview {
  state: InviteState;
  brandName: string;
  brandLogoUrl: string | null;
  inviterName: string;
  role: TeamRole;
  emailHint: string;
  expiresAt: string;
}

/** One of my pending invitations, for the brand switcher. */
export interface MyInvite {
  id: string;
  workspaceId: string;
  brandName: string;
  brandLogoUrl: string | null;
  inviterName: string;
  role: TeamRole;
  expiresAt: string;
}

export interface InviteMemberDto {
  email: string;
  role: TeamRole;
}

export interface UpdateMemberDto {
  role: TeamRole;
}

/**
 * The API names roles by the Prisma enum (EDITOR, CLIENT_VIEWER) and refuses
 * anything else, while the app uses short lowercase names and calls the
 * client role `client`. Invites and role changes used to send the app's names
 * straight through, so every one of them was refused with a 400; and the list
 * came back as `client_viewer`, which no label matched. Map both ways here, at
 * the one boundary, so nothing else has to know.
 */
const TO_API: Record<TeamRole, string> = {
  owner: 'OWNER',
  admin: 'ADMIN',
  editor: 'EDITOR',
  viewer: 'VIEWER',
  client: 'CLIENT_VIEWER',
  billing_admin: 'BILLING_ADMIN',
};

const FROM_API: Record<string, TeamRole> = Object.fromEntries(
  Object.entries(TO_API).map(([app, apiName]) => [apiName, app as TeamRole]),
);

export function toApiRole(role: TeamRole): string {
  return TO_API[role];
}

/** Accepts the API's name in any case; an unknown role reads as a viewer, the least privileged. */
export function fromApiRole(role: string): TeamRole {
  return FROM_API[role.toUpperCase()] ?? 'viewer';
}

type ApiTeamMember = Omit<TeamMember, 'role'> & { role: string };
type WithApiRole<T extends { role: TeamRole }> = Omit<T, 'role'> & { role: string };

function fromApiMember(member: ApiTeamMember): TeamMember {
  return { ...member, role: fromApiRole(member.role) };
}

function withRole<T extends { role: TeamRole }>(row: WithApiRole<T>): T {
  return { ...row, role: fromApiRole(row.role) } as T;
}

function fromApiSent(sent: Omit<InviteSent, 'invite'> & { invite: WithApiRole<TeamInvite> }): InviteSent {
  return { ...sent, invite: withRole<TeamInvite>(sent.invite) };
}

export const teamService = {
  async listMembers(): Promise<TeamMember[]> {
    const res = await api.get<ApiTeamMember[]>('/team');
    return (res.data ?? []).map(fromApiMember);
  },

  /** My own role on the brand in view; every role may read it. */
  async myMembership(): Promise<{ memberId: string | null; role: TeamRole }> {
    const res = await api.get<{ memberId: string | null; role: string }>('/team/me');
    return { memberId: res.data?.memberId ?? null, role: fromApiRole(res.data?.role ?? 'VIEWER') };
  },

  async seats(): Promise<TeamSeats | null> {
    const res = await api.get<TeamSeats>('/team/seats');
    return res.data ?? null;
  },

  async listInvites(): Promise<TeamInvite[]> {
    const res = await api.get<WithApiRole<TeamInvite>[]>('/team/invites');
    return (res.data ?? []).map((i) => withRole<TeamInvite>(i));
  },

  // The Team tab says what a plan or seat refusal means in its own words, so
  // no global upgrade toast goes on top of it (two toasts for one refusal).
  async inviteMember(dto: InviteMemberDto): Promise<InviteSent> {
    const res = await api.post<Omit<InviteSent, 'invite'> & { invite: WithApiRole<TeamInvite> }>(
      '/team/invite',
      { email: dto.email, role: toApiRole(dto.role) },
      { handlesPlanErrors: true },
    );
    if (!res.data) throw new Error('No invite in the answer');
    return fromApiSent(res.data);
  },

  async resendInvite(inviteId: string): Promise<InviteSent> {
    const res = await api.post<Omit<InviteSent, 'invite'> & { invite: WithApiRole<TeamInvite> }>(`/team/invites/${inviteId}/resend`, undefined, { handlesPlanErrors: true });
    if (!res.data) throw new Error('No invite in the answer');
    return fromApiSent(res.data);
  },

  async inviteLink(inviteId: string): Promise<string> {
    const res = await api.post<{ link: string }>(`/team/invites/${inviteId}/link`);
    return res.data?.link ?? '';
  },

  async revokeInvite(inviteId: string): Promise<void> {
    await api.delete(`/team/invites/${inviteId}`);
  },

  // The role change answers with the raw membership row: callers refetch the list.
  async updateMember(memberId: string, dto: UpdateMemberDto): Promise<void> {
    await api.patch(`/team/${memberId}/role`, { role: toApiRole(dto.role) });
  },

  async removeMember(memberId: string): Promise<void> {
    await api.delete(`/team/${memberId}`);
  },

  /** Leave the brand in view (any role but the owner's). */
  async leave(): Promise<void> {
    await api.delete('/team/me');
  },
};

/** The invitee's side (M-01): the public link page, accepting and declining. */
export const invitationsService = {
  async preview(token: string): Promise<InvitePreview> {
    const res = await api.get<WithApiRole<InvitePreview>>(`/invitations/${encodeURIComponent(token)}`);
    if (!res.data) throw new Error('No invitation');
    return withRole<InvitePreview>(res.data);
  },

  async accept(token: string): Promise<{ workspaceId: string }> {
    const res = await api.post<{ workspaceId: string }>(`/invitations/${encodeURIComponent(token)}/accept`);
    return { workspaceId: res.data?.workspaceId ?? '' };
  },

  async decline(token: string): Promise<void> {
    await api.post(`/invitations/${encodeURIComponent(token)}/decline`);
  },

  async mine(): Promise<MyInvite[]> {
    const res = await api.get<WithApiRole<MyInvite>[]>('/me/invites');
    return (res.data ?? []).map((i) => withRole<MyInvite>(i));
  },

  async acceptMine(id: string): Promise<{ workspaceId: string }> {
    const res = await api.post<{ workspaceId: string }>(`/me/invites/${id}/accept`);
    return { workspaceId: res.data?.workspaceId ?? '' };
  },

  async declineMine(id: string): Promise<void> {
    await api.post(`/me/invites/${id}/decline`);
  },

  async sendMyLink(id: string): Promise<{ emailSent: boolean }> {
    const res = await api.post<{ emailSent: boolean }>(`/me/invites/${id}/send-link`);
    return { emailSent: res.data?.emailSent === true };
  },
};
